import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { queryClient } from '../api/queryClient';
import { useAppStore } from '../store';
import type { CalendarNote } from '../types/calendar';

export function useCalendarNotes(date?: string) {
  const selectedDate = useAppStore((s) => s.selectedDate);
  const effectiveDate = date ?? selectedDate;

  return useQuery<CalendarNote[]>({
    queryKey: ['calendar-notes', effectiveDate],
    queryFn: () => api.calendarNotes.list(effectiveDate),
  });
}

export function useCalendarNoteByKey(meetingKey: string | null) {
  return useQuery<CalendarNote>({
    queryKey: ['calendar-notes', 'by-key', meetingKey],
    queryFn: () => api.calendarNotes.getByKey(meetingKey!),
    enabled: !!meetingKey,
    retry: false,
  });
}

export async function saveCalendarNote(data: { meetingKey: string; title: string; content: string; date: string }): Promise<CalendarNote> {
  try {
    const existing = await api.calendarNotes.getByKey(data.meetingKey);
    const updated = await api.calendarNotes.update(existing.id, { content: data.content });
    queryClient.invalidateQueries({ queryKey: ['calendar-notes'] });
    return updated;
  } catch {
    const created = await api.calendarNotes.create(data);
    queryClient.invalidateQueries({ queryKey: ['calendar-notes'] });
    return created;
  }
}

export async function updateCalendarNoteContent(id: string, content: string): Promise<CalendarNote> {
  const result = await api.calendarNotes.update(id, { content });
  queryClient.invalidateQueries({ queryKey: ['calendar-notes'] });
  return result;
}

export async function deleteCalendarNote(id: string) {
  await api.calendarNotes.delete(id);
  queryClient.invalidateQueries({ queryKey: ['calendar-notes'] });
}
